import Api from '@/api'
import * as types from '../mutations'
import ProductService from '@/services/products'
import BrandService from '@/services/brands'
import campaignService from '@/services/campaigns'
import ElabelService from '@/services/elabel'
import Vue from 'vue'
import axios from 'axios'

const inflightProductSearches = new Map()

const sortBrands = (brands = []) => {
  return [...brands].sort((firstBrand, secondBrand) => {
    return (firstBrand.name || '').localeCompare(secondBrand.name || '', undefined, { sensitivity: 'base' })
  })
}

const state = {
  products: [],
  fullProductList: [],
  selectedCampaign: {},
  selectedBrand: {},
  loadingBrands: true,
  filteredProductCount: 0,
  loadingProducts: true,
  brands: [],
  defaultBrand: {},
  consumerOptions: {}
}

const getters = {
  brands: (state) => state.brands,
  defaultBrand: (state) => state.defaultBrand,
  products: (state) => state.products,
  fullProductList: (state) => state.fullProductList,
  selectedCampaign: (state) => state.selectedCampaign,
  selectedBrand: (state) => state.selectedBrand,
  loadingBrands: (state) => state.loadingBrands,
  loadingProducts: (state) => state.loadingProducts,
  filteredProductCount: (state) => state.filteredProductCount,
  consumerOptions: (state) => state.consumerOptions,
  campaignRedirectionMode: (state) => {
    if (state.consumerOptions && state.consumerOptions.is_enabled && state.consumerOptions.consumer_url) {
      if (state.consumerOptions.consumer_url.indexOf(Api.default_stc_url) > -1) {
        return 'stc'
      } else if (state.consumerOptions.consumer_url.indexOf(Api.default_elabel_url) > -1 ||
        state.consumerOptions.consumer_url.indexOf(Api.remy_elabel_url) > -1) {
        return 'elabel'
      } else if (state.consumerOptions.consumer_url === '{product_url}') {
        return 'product'
      } else {
        return 'url'
      }
    } else if (state.consumerOptions && !state.consumerOptions.is_enabled) {
      return 'product'
    } else {
      return 'url'
    }
  }
}

const actions = {

  async loadBrands ({ commit }, options = {}) {
    commit(types.SET_BRANDS_LOADING, true)
    const brands = await BrandService.loadBrands(options)
    commit(types.SET_BRAND_LIST, brands)
    // Only refresh the default brand when no search text. Otherwise, searching for a brand would set the default for new products to whatever matched the search.
    if (!options.search) {
      commit(types.SET_DEFAULT_BRAND, brands[0])
    }
    commit(types.SET_BRANDS_LOADING, false)
  },

  async createBrand ({ commit }, { brand }) {
    try {
      const res = await BrandService.createBrand(brand)
      commit(types.CREATE_BRAND, res)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = brand
      return Promise.reject(err.response)
    }
  },

  async updateBrand ({ commit }, { brand }) {
    try {
      const res = await BrandService.updateBrand(brand)
      commit(types.UPDATE_BRAND, res)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = brand
      return Promise.reject(err.response)
    }
  },

  async deleteBrand ({ commit }, { brand }) {
    try {
      const res = await BrandService.deleteBrand(brand.id)
      commit(types.DELETE_BRAND, brand)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = brand
      return Promise.reject(err.response)
    }
  },

  async loadFullProductList ({ commit, dispatch }, { offset, loadedList = [] }) {
    const res = await ProductService.loadProducts({ limit: 500, offset: offset || 0 })
    loadedList = loadedList.concat(res.results)
    // Setting a limit to 4000 for now to not load everything
    if (loadedList.length < res.count && res.count <= 4000) {
      await dispatch('loadFullProductList', { offset: offset ? offset + 500 : 500, loadedList: loadedList })
    } else {
      commit(types.SET_FULL_PRODUCT_LIST, loadedList)
      return loadedList
    }
  },

  async loadSelectedCampaignConsumerOptions ({ commit }, campaign) {
    const res = await campaignService.loadConsumerOptions(campaign)
    commit(types.SET_CAMPAIGN_CONSUMER_OPTIONS, res)
  },

  async searchProducts ({ commit }, { campaign, offset, limit, search }) {
    const key = JSON.stringify({ campaign, offset, limit, search })
    if (inflightProductSearches.has(key)) {
      return inflightProductSearches.get(key)
    }
    const promise = ProductService.loadProducts({ campaign, offset, limit, search })
      .then(res => res.results)
      .finally(() => inflightProductSearches.delete(key))
    inflightProductSearches.set(key, promise)
    return promise
  },

  async loadProducts ({ commit }, { campaign, brand, offset, limit, search, cancelToken }) {
    commit(types.SET_PRODUCT_LOADING, true)
    try {
      const res = await ProductService.loadProducts({ campaign, brand, offset, limit, search, cancelToken })
      if (res.results) {
        res.results.forEach((product) => {
          product.code_count = 0
        })
      }
      commit(types.SET_PRODUCT_LIST, { products: res.results, count: res.count })
    } catch (err) {
      if (axios.isCancel(err)) return
      console.error('loadProducts failed:', err)
      throw err
    } finally {
      commit(types.SET_PRODUCT_LOADING, false)
    }
  },

  async loadProductBySKU ({ commit }, product) {
    try {
      const res = await ProductService.loadProducts({ sku: product.sku })
      if (res && res.results && res.results[0]) {
        return Promise.resolve(res.results[0])
      } else {
        return Promise.resolve(product)
      }
    } catch (err) {
      return Promise.reject(err)
    }
  },

  async loadProduct ({ commit, getters }, id) {
    const res = await ProductService.loadProduct(id)
    res.code_count = 0
    commit(types.UPDATE_PRODUCT, res)
    return res
  },

  async createProduct ({ commit, getters, rootGetters }, { product }) {
    try {
      const res = await ProductService.createProduct(product)

      if ((rootGetters.campaigns.length > 1 && !getters.selectedBrand.id) || (product.brand === getters.selectedBrand.id)) {
        commit(types.CREATE_PRODUCT, res)
        commit(types.INCREASE_FILTERED_PRODUCT_COUNT)
      }

      return Promise.resolve(res)
    } catch (err) {
      err.response.request = product
      return Promise.reject(err.response)
    }
  },

  async deleteProductPicture ({ commit }, { product }) {
    try {
      const res = await ProductService.deleteProductPicture(product.id)
      commit(types.UPDATE_PRODUCT, { ...product, image: null })
      return Promise.resolve(res)
    } catch (err) {
      return Promise.reject(err.response)
    }
  },

  async updateProduct ({ commit }, { product }) {
    try {
      const res = await ProductService.updateProduct(product)
      // Keep old code count to prevent re-fetch
      res.code_count = product.code_count
      commit(types.UPDATE_PRODUCT, res)
      return Promise.resolve(res)
    } catch (err) {
      err.response.request = product
      return Promise.reject(err.response)
    }
  },

  async publishElabel ({ commit }, { product }) {
    await ElabelService.publishElabel(product.id)
    commit(types.UPDATE_ELABEL_STATUS, { product, isPublished: true })
  },

  async unpublishElabel ({ commit }, { product }) {
    await ElabelService.unpublishElabel(product.id)
    commit(types.UPDATE_ELABEL_STATUS, { product, isPublished: false })
  }

}

const mutations = {
  [types.SET_PRODUCT_LIST] (state, { products, count }) {
    state.products = products
    state.filteredProductCount = count
  },

  [types.SET_FULL_PRODUCT_LIST] (state, products) {
    state.fullProductList = products
  },

  [types.INCREASE_FILTERED_PRODUCT_COUNT] (state) {
    state.filteredProductCount += 1
  },

  [types.SET_PRODUCT_LOADING] (state, loading) {
    state.loadingProducts = loading
  },

  [types.SET_SELECTED_CAMPAIGN] (state, campaign) {
    state.selectedCampaign = campaign
  },

  [types.SET_CAMPAIGN_CONSUMER_OPTIONS] (state, options) {
    state.consumerOptions = options
  },

  [types.CREATE_PRODUCT] (state, product) {
    product.code_count = 0
    state.products.unshift(product)
  },

  [types.INCREASE_PRODUCT_CODE_COUNT] (state, product) {
    const index = state.products.findIndex((e) => e.id === product)
    if (~index) {
      state.products[index].code_count += 1
    }
  },

  [types.UPDATE_ELABEL_STATUS] (state, { product, isPublished }) {
    const index = state.products.findIndex((e) => e.id === product.id)
    if (~index) {
      Vue.set(state.products[index].elabel, 'is_published', isPublished)
    }
  },

  [types.UPDATE_PRODUCT] (state, product) {
    const index = state.products.findIndex((e) => e.id === product.id)
    if (~index) {
      // If a product is transferred out of the selected campaign/brand, drop the
      // row instead of updating it in place — otherwise the transfer leaves a stale row
      // that only clears on the next reload / brand re-select.
      const movedToOtherCampaign = product.campaign && product.campaign.id !== state.selectedCampaign.id
      const movedToOtherBrand = state.selectedBrand && state.selectedBrand.id && product.brand && product.brand.id !== state.selectedBrand.id
      if (movedToOtherCampaign || movedToOtherBrand) {
        state.products.splice(index, 1)
        if (state.filteredProductCount > 0) {
          state.filteredProductCount -= 1
        }
      } else {
        Vue.set(state.products, index, product)
      }
    }
  },

  [types.SET_PRODUCT_CODE_COUNT] (state, { product, count }) {
    const index = state.products.findIndex((e) => e.id === product)
    if (~index) {
      state.products[index].code_count = count
    }
  },

  [types.SET_BRANDS_LOADING] (state, loading) {
    state.loadingBrands = loading
  },

  [types.SET_BRAND_LIST] (state, brands) {
    state.brands = sortBrands(brands)
  },

  [types.CREATE_BRAND] (state, brand) {
    state.brands = sortBrands([...state.brands, brand])
  },

  [types.UPDATE_BRAND] (state, brand) {
    const index = state.brands.findIndex((e) => e.id === brand.id)
    if (~index) {
      state.brands.splice(index, 1, { ...brand })
      state.brands = sortBrands(state.brands)
    }
  },

  [types.DELETE_BRAND] (state, brand) {
    const remainingBrands = state.brands.filter((item) => item.id !== brand.id)
    state.brands = remainingBrands

    if (state.defaultBrand && state.defaultBrand.id === brand.id) {
      state.defaultBrand = remainingBrands[0] || {}
    }

    if (state.selectedBrand && state.selectedBrand.id === brand.id) {
      state.selectedBrand = remainingBrands[0] || {}
    }
  },

  [types.SET_DEFAULT_BRAND] (state, brand) {
    state.defaultBrand = brand
  },

  [types.SET_SELECTED_BRAND] (state, brand) {
    state.selectedBrand = brand
  }

}

export default {
  state,
  getters,
  actions,
  mutations
}
